import dayjs, { Dayjs } from "dayjs";
import { ChartType, Plugin } from "chart.js";
import ClickhouseDateTimeGranularity = App.Enums.ClickhouseDateTimeGranularity;

export interface ScaleWeekTicksPluginOptions {
	scaleId: string;
	granularity?: ClickhouseDateTimeGranularity;
}

declare module "chart.js" {
	interface PluginOptionsByType<TType extends ChartType> {
		scaleWeekTicks: ScaleWeekTicksPluginOptions;
	}
}

interface DateParserFunc {
	(str: string): Dayjs;
}

export function granularityToDateParser(
	granularity: ClickhouseDateTimeGranularity
): DateParserFunc | null {
	if (granularity.includes("min")) {
		return null;
	}

	if (granularity === "week") {
		return (str) => {
			const parts = str.split("-");

			return dayjs().year(parts[0]).isoWeek(parts[1]).startOf("isoWeek");
		};
	}

	if (granularity === "month") {
		return (str) => {
			const parts = str.split("-");

			return dayjs()
				.startOf("month")
				.year(parts[0])
				.month(parts[1] - 1);
		};
	}

	return (str) => dayjs(str);
}

/*
This plugin hacks the ticks such that the week start tick is being drawn on chart.
It overrides the tick start to be equal to what normally would be the grid line.
The grid lines are not drawn because drawOnChartArea = false.

For dates we implemented custom logic when the labels overlap.
In this case a property _keep is added.
Only the ticks with _keep should be drawn, and also as a grid line.
 */
export const scaleWeekTicksPlugin = {
	id: "scaleWeekTicks",
	beforeDraw(chart, args, options: ScaleWeekTicksPluginOptions) {
		const scale = chart.scales[options.scaleId];

		if (scale._gridLineItems) {
			return;
		}

		const granularity = options.granularity;
		if (!granularity || granularity.includes("min")) {
			return;
		}

		let startOfPeriod = "isoWeek";
		let sameUnit = "day";
		let dateParser = granularityToDateParser(granularity);

		if (granularity === "week") {
			return;
		} else if (granularity === "month") {
			startOfPeriod = "year";
			sameUnit = "month";
			dateParser = (str) => granularityToDateParser(granularity)(str);
		}

		scale._gridLineItems = scale._computeGridLineItems(chart.chartArea);
		for (const i in scale.ticks) {
			const tick = scale.ticks[i];
			const date = dateParser(scale.getLabelForValue(tick.value));

			if (scale.$ticksAreLimited) {
				if (!tick._keep) {
					scale._gridLineItems[i].width = scale._gridLineItems[i].tickWidth = 0;
					continue;
				}
			} else if (!date.isSame(date.clone().startOf(startOfPeriod), sameUnit)) {
				continue;
			}

			scale._gridLineItems[i].tx1 = scale._gridLineItems[i].x1;
			scale._gridLineItems[i].ty1 = scale._gridLineItems[i].y1;
		}
	},
} as Plugin;
